'use client';

import { PersonalDataConsentText, MarketingConsentText, PrivacyPolicyNotice } from '@/components/LegalConsentText';
import React, { useEffect, useState } from 'react';
import { AlertCircle, Download } from 'lucide-react';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { CaptchaDialog as Dialog, CaptchaDialogContent as DialogContent } from '@/components/SmartCaptchaDialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VisibleSmartCaptcha } from '@/components/SmartCaptcha';

export interface LeadContact {
  name: string;
  phone: string;
  email: string;
  privacy: boolean;
  marketing: boolean;
}

import { useLeadFormAnalytics } from '@/lib/analytics/use-lead-form-analytics';
import { formatRussianPhone } from '@/lib/forms/contact-validation';

const INITIAL: LeadContact = {
  name: '',
  phone: '',
  email: '',
  privacy: false,
  marketing: false,
};

const fieldClassName =
  'mt-1.5 flex h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-950 md:text-sm';

export default function LeadGateModal({
  open,
  onOpenChange,
  onConfirm,
  privacyPolicyUrl,
}: {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  /** Отправка лида на бэкенд + генерация PDF. Возвращает результат. */
  onConfirm: (contact: LeadContact, smartToken: string) => Promise<{ ok: boolean; error?: string }>;
  privacyPolicyUrl: string;
}) {
  const analytics = useLeadFormAnalytics('calculator-commercial-offer', false);
  const [contact, setContact] = useState<LeadContact>(INITIAL);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [smartToken, setSmartToken] = useState('');
  const [captchaAttempt, setCaptchaAttempt] = useState(0);
  const [challengeVisible, setChallengeVisible] = useState(false);
  useEffect(() => {
    if (!open) { setChallengeVisible(false); setSmartToken(''); }
  }, [open]);

  const update = (field: keyof LeadContact, value: string | boolean) => {
    setError('');
    setContact((prev) => ({ ...prev, [field]: field === 'phone' && typeof value === 'string' ? formatRussianPhone(value) : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!contact.phone.trim()) {
      setError('Укажите телефон — на него пришлём КП и уточним детали.');
      return;
    }
    if (
      contact.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())
    ) {
      setError('Некорректный формат email.');
      return;
    }
    if (!contact.privacy) {
      setError('Необходимо согласие на обработку персональных данных.');
      return;
    }

    if (!smartToken) {
      setError('Подтвердите, что вы не робот.');
      return;
    }

    setIsSubmitting(true);
    const result = await onConfirm(contact, smartToken);
    setIsSubmitting(false);
    setSmartToken('');
    setCaptchaAttempt((value) => value + 1);

    if (result.ok) {
      setContact(INITIAL);
      onOpenChange(false);
    } else {
      setError(result.error || 'Не удалось отправить заявку. Попробуйте ещё раз.');
    }
  };

  return (
    <Dialog challengeVisible={challengeVisible} busy={isSubmitting} open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:max-w-md sm:p-6"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900">
            Получить КП и скачать PDF
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Укажите телефон — пришлём готовое коммерческое предложение и при
            необходимости уточним детали. PDF скачается сразу после отправки формы.
          </DialogDescription>
        </DialogHeader>

        <form onFocusCapture={analytics.onFocusCapture} onChangeCapture={analytics.onChangeCapture} onInvalidCapture={analytics.onInvalidCapture} onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div>
            <Label htmlFor="lead-name">ФИО</Label>
            <Input
              id="lead-name"
              value={contact.name}
              onChange={(e) => update('name', e.target.value)}
              className={fieldClassName}
              placeholder="Иванов Иван Иванович"
            />
          </div>
          <div>
            <Label htmlFor="lead-phone">
              Телефон <span className="text-red-500">*</span>
            </Label>
            <Input
              id="lead-phone"
              value={contact.phone}
              onChange={(e) => update('phone', e.target.value)}
              className={fieldClassName}
              placeholder="+7 (999) 123-45-67"
              required
            />
          </div>
          <div>
            <Label htmlFor="lead-email">Email</Label>
            <Input
              id="lead-email"
              type="email"
              value={contact.email}
              onChange={(e) => update('email', e.target.value)}
              className={fieldClassName}
              placeholder="example@mail.ru"
            />
          </div>

          <div className="flex items-start">
            <Checkbox
              id="lead-privacy"
              checked={contact.privacy}
              onCheckedChange={(checked) => update('privacy', checked === true)}
              className="mt-1"
            />
            <div className="min-w-0 flex-1">
                <Label htmlFor="lead-privacy" className="ml-2 text-sm leading-5 text-slate-600">
                  <PersonalDataConsentText />
                </Label>
                <PrivacyPolicyNotice />
                </div>
          </div>

          <VisibleSmartCaptcha key={captchaAttempt} onTokenChange={setSmartToken} onChallengeChange={setChallengeVisible} />

          {error && (
            <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-[#214688] text-white hover:bg-[#1a3870]"
            disabled={isSubmitting || !smartToken}
          >
            {isSubmitting ? 'Готовим КП...' : 'Отправить и скачать PDF'}
            {!isSubmitting && <Download className="ml-1 h-4 w-4" />}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
